import * as React from "react";
import { CQA_COMPLETE, CQA_APPROVED, IN_CQA } from '@codametrix/ui-common';
import { fetchApi, shouldAttachBearerAuthorization } from '@codametrix/client-api';
import {
  Typography,
  Button,
  DialogTitle,
  Dialog,
  TextField,
  Alert,
  FormControl,
  RadioGroup,
  Radio,
  FormControlLabel,
  Snackbar,
  Alert as MuiAlert
} from "@mui/material";
import { styled, createTheme, ThemeProvider } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

type RevertTarget = typeof CQA_COMPLETE | typeof IN_CQA;

type Props = {
  selected: any[];
  open: boolean;
  handleClose: () => void;
  handleAfterRevert: () => void;
  cqaWorkitemStatus?: string;
};

const defaultTheme = createTheme();

const StyledButton = styled(Button)({
  borderRadius: "4px",
  textTransform: "none",
  flexGrow: 1,
  maxWidth: "100px",
  maxHeight: "40px",
  minWidth: "100px",
  minHeight: "40px"
});

type CoderNoteBatchEntry = {
  note_content: string;
  lookup_reference: string;
  lookup_type: string;
  event_data?: Record<string, string>;
};

const buildHeaders = () => {
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/json"
  };
  if (shouldAttachBearerAuthorization()) {
    headers.Authorization = `${sessionStorage.getItem("authToken")}`;
  }
  return headers;
};

async function apiBulkUpdateGraderWorkitems(
  updates: Array<{ sdCaseId: string; status: string }>
) {
  const response = await fetchApi("/api/cqa-service/v1/grader-workitem/bulk", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify({ updates })
  });

  if (!response.ok) {
    const text = await response.text();
    let msg = `Failed to update grader work items. Status: ${response.status}`;
    try {
      const json = JSON.parse(text);
      if (json.reason) msg = json.reason;
    } catch {}
    throw new Error(msg);
  }

  return response.json();
}

async function apiBatchAddCoderNotes(notes: CoderNoteBatchEntry[]) {
  if (!notes || notes.length === 0) return;
  const response = await fetchApi("/coding-api/coder_notes/batch/v1", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify(notes)
  });
  if (!response.ok) throw new Error(`Failed to save notes. Status: ${response.status}`);
  return response.json();
}

async function saveRevertComment(
  comment: string,
  selected: any[],
  revertTarget: RevertTarget
): Promise<boolean> {
  const trimmed = comment.trim();
  if (trimmed === "") return true;

  const notes: CoderNoteBatchEntry[] = selected.map((item: any) => ({
    note_content: trimmed,
    lookup_reference: item.sd_case_id || item.case_id,
    lookup_type: "CQA_FEEDBACK_COMMENT",
    event_data: {
      old_status: item.qa_status,
      new_status:
        item.qa_status === CQA_APPROVED ? revertTarget : IN_CQA
    }
  }));

  try {
    await apiBatchAddCoderNotes(notes);
    return true;
  } catch {
    return false;
  }
}

export const RevertApproval = ({
  selected,
  open,
  handleClose,
  handleAfterRevert,
  cqaWorkitemStatus
}: Props) => {
  const MIN_COMMENT_WORDS = 2;

  const [isReverting, setIsReverting] = React.useState(false);
  const [comment, setComment] = React.useState("");
  const [revertTarget, setRevertTarget] = React.useState<RevertTarget>(CQA_COMPLETE);
  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "warning" | "error";
  }>({ open: false, message: "", severity: "success" });

  const wordCount = comment.trim() === "" ? 0 : comment.trim().split(/\s+/).length;
  const commentIsInvalid = comment.trim() !== "" && wordCount < MIN_COMMENT_WORDS;

  const handleDialogClose = () => {
    setComment("");
    setRevertTarget(CQA_COMPLETE);
    handleClose();
  };

  const handleRevertClick = async () => {
    if (!selected || selected.length === 0 || commentIsInvalid) return;

    const updates = selected.map((item: any) => ({
      sdCaseId: item.sd_case_id || item.case_id,
      status: item.qa_status === CQA_APPROVED ? revertTarget : IN_CQA
    }));

    setIsReverting(true);

    try {
      await apiBulkUpdateGraderWorkitems(updates);

      const noteSaved = await saveRevertComment(comment, selected, revertTarget);

      setSnackbar({
        open: true,
        message: `${selected.length} work item${selected.length > 1 ? "s" : ""} ${
          noteSaved
            ? "successfully reverted to Complete status."
            : "reverted, but comment failed to save."
        }`,
        severity: noteSaved ? "success" : "warning"
      });

      handleAfterRevert();
      handleDialogClose();
    } catch {
      setSnackbar({
        open: true,
        message: "Failed to revert work items. Please try again.",
        severity: "error"
      });
    } finally {
      setIsReverting(false);
    }
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="sm"
        sx={{ "& .MuiDialogContent-root": { padding: 2 } }}>
        <DialogTitle sx={{ m: 0 }} id="revert-approval-title">
          {cqaWorkitemStatus === CQA_COMPLETE ? "Revert Submission" : "Revert Approval"}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleDialogClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500]
          }}
          disabled={isReverting}>
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Alert
            hidden={true}
            severity={!selected || selected?.length === 0 ? "warning" : "info"}
            sx={{ mb: 2 }}>
            {selected ? selected.length : 0} work item
            {selected && selected.length !== 1 ? "s" : ""} selected
          </Alert>
          {cqaWorkitemStatus === CQA_APPROVED && (
            <FormControl sx={{ mb: 2 }}>
              <RadioGroup
                row
                name="revertTargetRadioGroup"
                value={revertTarget}
                onChange={e => setRevertTarget(e.target.value as RevertTarget)}
                sx={{ gap: "24px" }}>
                <FormControlLabel
                  value={CQA_COMPLETE}
                  control={<Radio />}
                  label="Back to complete"
                />
                <FormControlLabel
                  value={IN_CQA}
                  control={<Radio />}
                  label="Back to in progress"
                />
              </RadioGroup>
            </FormControl>
          )}
          <Typography gutterBottom sx={{ mb: 2 }}>
            {(() => {
              const approved =
                selected?.filter((i: any) => i.qa_status === CQA_APPROVED) ?? [];
              const complete =
                selected?.filter((i: any) => i.qa_status === CQA_COMPLETE) ?? [];
              return (
                <>
                  {approved.length > 0 && (
                    <>
                      You are about to revert {approved.length}{" "}
                      <strong>approved</strong> work item
                      {approved.length === 1 ? "" : "s"} back to{" "}
                      <strong>
                        {revertTarget === IN_CQA ? "in progress" : "complete"}
                      </strong>{" "}
                      status.
                      {revertTarget === IN_CQA &&
                        " This will allow coders to edit them again before resubmission."}
                    </>
                  )}
                  {approved.length > 0 && complete.length > 0 && (
                    <>
                      <br />
                      <br />
                    </>
                  )}
                  {complete.length > 0 && (
                    <>
                      You are about to revert {complete.length}{" "}
                      <strong>complete</strong> work item
                      {complete.length === 1 ? "" : "s"} back to{" "}
                      <strong>in progress</strong> status. This will allow coders
                      to edit them again before resubmission.
                    </>
                  )}
                </>
              );
            })()}
          </Typography>
          <TextField
            label="Comment (optional)"
            multiline
            rows={3}
            fullWidth
            value={comment}
            onChange={e => setComment(e.target.value)}
            sx={{ mb: 2 }}
            disabled={isReverting}
            error={commentIsInvalid}
            helperText={
              commentIsInvalid
                ? `Please enter at least ${MIN_COMMENT_WORDS} words (${wordCount}/${MIN_COMMENT_WORDS})`
                : undefined
            }
          />
          <Typography>Are you sure you want to proceed?</Typography>
        </DialogContent>
        <DialogActions>
          <StyledButton
            variant="outlined"
            onClick={handleDialogClose}
            disabled={isReverting}>
            Cancel
          </StyledButton>
          <StyledButton
            variant="contained"
            disabled={
              !selected || selected.length === 0 || isReverting || commentIsInvalid
            }
            sx={{ ml: 5 }}
            onClick={handleRevertClick}>
            {isReverting ? "Reverting..." : "Revert"}
          </StyledButton>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <MuiAlert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled">
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </ThemeProvider>
  );
};
